import moment, { Moment } from 'moment';
import { useEffect, useState } from 'react';

import { parseDateToString } from '@utils/helpers';
import { useRouter } from 'shared-routing';
import { SelectOptionType } from 'shared-types/Common';

type UseSpaEntityFilterParams = {
  categoryId: number | null;
  arrivalDate: string | undefined;
  fromDate: string | undefined;
  changeJourneyParam: (dateId: string, date?: string | Moment | number) => void;
  listingRoute: string;
};

export function useSpaEntityFilter({
  categoryId,
  arrivalDate,
  fromDate,
  changeJourneyParam,
  listingRoute,
}: UseSpaEntityFilterParams) {
  const router = useRouter();

  const today = moment().format();
  const initialDate = arrivalDate || fromDate || today;

  const [categoryFilterId, setCategoryFilterId] = useState<number | null>(categoryId);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    categoryId ? String(categoryId) : null
  );
  const [pickerDate, setPickerDate] = useState(initialDate);
  const [appliedDate, setAppliedDate] = useState(initialDate);

  useEffect(() => {
    if (arrivalDate) {
      setAppliedDate(arrivalDate);
      setPickerDate(arrivalDate);
    }
  }, [arrivalDate]);

  const appliedDateString = appliedDate
    ? moment(appliedDate).format('YYYY-MM-DD')
    : moment().format('YYYY-MM-DD');

  const handleCategoryChange = ({ value }: SelectOptionType): void => {
    const newValue = value ? String(value) : null;
    setSelectedCategory(newValue);
  };

  const handleUpdate = () => {
    setAppliedDate(pickerDate);

    const params = {
      category: selectedCategory ? selectedCategory : '',
      arrivalDate: parseDateToString(pickerDate),
    };
    router.push({ pathname: listingRoute, query: params as Record<string, string> }, undefined, {
      shallow: true,
    });

    if (String(selectedCategory) !== String(categoryFilterId)) {
      setCategoryFilterId(
        !selectedCategory || isNaN(Number(selectedCategory)) ? null : Number(selectedCategory)
      );
    }

    changeJourneyParam('spaDate', pickerDate);
    changeJourneyParam('category', selectedCategory ?? undefined);
  };

  const handleDirectCategoryChange = (value: string) => {
    const numericValue = !value || isNaN(Number(value)) ? null : Number(value);
    setCategoryFilterId(numericValue);
    setSelectedCategory(value || null);
    changeJourneyParam('category', value || undefined);

    router.push(
      {
        pathname: router.pathname,
        query: { ...router.query, category: value || undefined },
      },
      undefined,
      { shallow: true }
    );
  };

  return {
    categoryFilterId,
    appliedDateString,
    pickerDate,
    selectedCategory,
    setPickerDate,
    handleCategoryChange,
    handleDirectCategoryChange,
    handleUpdate,
  };
}
